/**
 * Mobile Service - APIs para aplicação mobile
 */
import apiClient from './apiClient';
import { Hospital } from './hospitalService';
import { Product } from './productService';
import { addDebugLog } from '../components/DebugPanel';

// Debug logging function
const debugLog = (entry: any) => {
  addDebugLog(entry);
};

// Mobile detection
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// ========== INTERFACES ==========

export interface Location {
  id: number;
  nome: string;
  descricao?: string;
}

export interface Job {
  id: number;
  hospital_id: number;
  hospital_nome: string;
  location_id: number;
  location_nome: string;
  operador_id: number;
  operador_nome: string;
  titulo: string;
  descricao?: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  data_prevista: string;
  data_inicio?: string;
  data_fim?: string;
  total_items_counted: number;
  total_quantity_counted: number;
}

export interface JobCreateInput {
  hospital_id: number;
  location_id: number;
  operador_id: number;
  titulo: string;
  descricao?: string;
  data_prevista: string; // ISO date format
  product_ids?: number[];
}

export interface CountEntry {
  produto_codigo: string;
  quantidade: number;
  observacoes?: string;
  latitude?: number;
  longitude?: number;
}

export interface CountBatchUpload {
  counts: CountEntry[];
}

export interface JobDownloadData {
  job: {
    id: number;
    titulo: string;
    descricao?: string;
    status: string;
    data_prevista: string;
    hospital_id: number;
    location_id: number;
  };
  hospital: {
    id: number;
    codigo: string;
    nome: string;
    endereco?: string;
  };
  location: {
    id: number;
    nome: string;
    descricao?: string;
  };
  locations: Location[];
  products: Product[];
  download_timestamp: string;
}

// ========== API METHODS ==========

/**
 * Get all hospitals for mobile selection
 */
export const getHospitals = async (): Promise<Hospital[]> => {
  const startTime = Date.now();

  debugLog({
    level: 'info',
    category: 'sync',
    message: '📊 MOBILE DEBUG: Fetching hospitals...',
    data: {
      endpoint: '/hospitals',
      method: 'GET',
      isMobile: isMobileDevice(),
      timestamp: new Date().toISOString()
    }
  });

  try {
    const response = await apiClient.get<Hospital[]>('/hospitals');
    const duration = Date.now() - startTime;

    debugLog({
      level: 'success',
      category: 'sync',
      message: `✅ MOBILE DEBUG: ${response.data.length} hospitals fetched successfully`,
      data: {
        hospitalCount: response.data.length,
        duration: `${duration}ms`,
        hospitals: response.data.map(h => ({
          id: h.id,
          name: h.nome_fantasia,
          codigo: h.codigo
        }))
      }
    });

    return response.data;
  } catch (error: any) {
    const duration = Date.now() - startTime;

    debugLog({
      level: 'error',
      category: 'sync',
      message: `❌ MOBILE DEBUG: Failed to fetch hospitals after ${duration}ms`,
      data: {
        error: error.message,
        duration: `${duration}ms`,
        status: error.response?.status,
        statusText: error.response?.statusText,
        endpoint: '/hospitals',
        isNetworkError: !error.response
      }
    });

    throw new Error(error.response?.data?.detail || 'Failed to fetch hospitals');
  }
};

/**
 * Get locations for a specific hospital
 */
export const getLocations = async (hospitalId: number): Promise<Location[]> => {
  const startTime = Date.now();

  debugLog({
    level: 'info',
    category: 'sync',
    message: `📍 MOBILE DEBUG: Fetching locations for hospital ${hospitalId}...`,
    data: {
      hospitalId,
      endpoint: `/hospitals/${hospitalId}/locations`,
      method: 'GET',
      isMobile: isMobileDevice(),
      timestamp: new Date().toISOString()
    }
  });

  try {
    const response = await apiClient.get<Location[]>(`/hospitals/${hospitalId}/locations`);
    const duration = Date.now() - startTime;

    debugLog({
      level: 'success',
      category: 'sync',
      message: `✅ MOBILE DEBUG: ${response.data.length} locations fetched successfully`,
      data: {
        hospitalId,
        locationCount: response.data.length,
        duration: `${duration}ms`,
        locations: response.data.map(l => ({
          id: l.id,
          name: l.nome,
          descricao: l.descricao
        }))
      }
    });

    return response.data;
  } catch (error: any) {
    const duration = Date.now() - startTime;

    debugLog({
      level: 'error',
      category: 'sync',
      message: `❌ MOBILE DEBUG: Failed to fetch locations for hospital ${hospitalId} after ${duration}ms`,
      data: {
        hospitalId,
        error: error.message,
        duration: `${duration}ms`,
        status: error.response?.status,
        statusText: error.response?.statusText,
        endpoint: `/hospitals/${hospitalId}/locations`,
        isNetworkError: !error.response
      }
    });

    throw new Error(error.response?.data?.detail || 'Failed to fetch locations');
  }
};

/**
 * Search product by barcode
 */
export const searchProductByCode = async (codigo: string): Promise<Product> => {
  const startTime = Date.now();

  debugLog({
    level: 'info',
    category: 'sync',
    message: `🔍 MOBILE DEBUG: Searching product by code ${codigo}...`,
    data: {
      codigo,
      endpoint: `/products/by-code/${codigo}`,
      method: 'GET',
      isMobile: isMobileDevice(),
      timestamp: new Date().toISOString()
    }
  });

  try {
    const response = await apiClient.get<Product>(`/products/by-code/${codigo}`);
    const duration = Date.now() - startTime;

    debugLog({
      level: 'success',
      category: 'sync',
      message: `✅ MOBILE DEBUG: Product found: ${response.data.nome}`,
      data: {
        codigo,
        productId: response.data.id,
        productName: response.data.nome,
        duration: `${duration}ms`,
        product: {
          id: response.data.id,
          codigo: response.data.codigo,
          nome: response.data.nome,
          unidade: response.data.unidade
        }
      }
    });

    return response.data;
  } catch (error: any) {
    const duration = Date.now() - startTime;

    debugLog({
      level: error.response?.status === 404 ? 'warning' : 'error',
      category: 'sync',
      message: `❌ MOBILE DEBUG: Product search failed for code ${codigo} after ${duration}ms`,
      data: {
        codigo,
        error: error.message,
        duration: `${duration}ms`,
        status: error.response?.status,
        statusText: error.response?.statusText,
        endpoint: `/products/by-code/${codigo}`,
        isNotFound: error.response?.status === 404,
        isNetworkError: !error.response
      }
    });

    if (error.response?.status === 404) {
      throw new Error(`Produto com código ${codigo} não encontrado`);
    }
    throw new Error(error.response?.data?.detail || 'Failed to search product');
  }
};

/**
 * List jobs for current user
 */
export const listJobs = async (status?: string): Promise<Job[]> => {
  try {
    const params = status ? { status } : {};
    const response = await apiClient.get<Job[]>('/jobs', { params });
    return response.data;
  } catch (error: any) {
    console.error('Error fetching jobs:', error);
    throw new Error(error.response?.data?.detail || 'Failed to fetch jobs');
  }
};

/**
 * Create a new job
 */
export const createJob = async (data: JobCreateInput): Promise<Job> => {
  try {
    const response = await apiClient.post<Job>('/jobs', data);
    return response.data;
  } catch (error: any) {
    console.error('Error creating job:', error);
    throw new Error(error.response?.data?.detail || 'Failed to create job');
  }
};

/**
 * Start a job (change status to IN_PROGRESS)
 */
export const startJob = async (jobId: number): Promise<{success: boolean; message: string}> => {
  try {
    const response = await apiClient.put<{success: boolean; message: string}>(`/jobs/${jobId}/start`);
    return response.data;
  } catch (error: any) {
    console.error(`Error starting job ${jobId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to start job');
  }
};

/**
 * Finish a job (change status to COMPLETED)
 */
export const finishJob = async (jobId: number): Promise<{success: boolean; message: string}> => {
  try {
    const response = await apiClient.put<{success: boolean; message: string}>(`/jobs/${jobId}/finish`);
    return response.data;
  } catch (error: any) {
    console.error(`Error finishing job ${jobId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to finish job');
  }
};

/**
 * Upload count entries in batch
 */
export const uploadCounts = async (jobId: number, counts: CountEntry[]): Promise<any> => {
  try {
    const response = await apiClient.post(`/jobs/${jobId}/counts/upload`, { counts });
    return response.data;
  } catch (error: any) {
    console.error(`Error uploading counts for job ${jobId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to upload counts');
  }
};

/**
 * Download job data for offline use
 */
export const downloadJobData = async (jobId: number): Promise<JobDownloadData> => {
  try {
    const response = await apiClient.get<JobDownloadData>(`/jobs/${jobId}/download`);
    return response.data;
  } catch (error: any) {
    console.error(`Error downloading job ${jobId}:`, error);
    throw new Error(error.response?.data?.detail || 'Failed to download job data');
  }
};

// Export default service object
const mobileService = {
  getHospitals,
  getLocations,
  searchProductByCode,
  listJobs,
  createJob,
  startJob,
  finishJob,
  uploadCounts,
  downloadJobData,
};

export default mobileService;
